import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { RadioOption, RadioOptionsInput } from "../RadioOptionsInput";
import {
  CONTRACT_STATUS_TYPES,
  EXPORT_DATA_TYPES,
  EXPORT_INCLUSION_TYPES,
  ExportFieldConfiguration,
  EXPORT_FIELDS_INCLUSION_TYPES,
} from "./types";
import { getCommonExportRatioOptions } from "./utils";
import { ExportDataTypeInclusionSelector } from "./ExportDataTypeInclusionSelector";
import { FieldConfigurationSelector } from "./FieldConfigurationSelector";
import {
  contractExportFields,
  firmographicsExportFields,
} from "./exportConfigurationFields";

interface Props {
  inclusion: EXPORT_INCLUSION_TYPES;
  setInclusion?: (value: EXPORT_INCLUSION_TYPES) => void;
  universeEnabled?: boolean;
  contractFieldConfiguration?: ExportFieldConfiguration;
  setContractFieldConfiguration?: (cfg: ExportFieldConfiguration) => void;
  enforceMandatoryFields?: boolean;
  contractStatus?: CONTRACT_STATUS_TYPES;
  setContractStatus?: (status: CONTRACT_STATUS_TYPES) => void;
}

// Universe only: the status options narrow "Include all Contracts", so each
// one is an inclusion of ALL plus a contract status.
const NONE_OPTION = EXPORT_INCLUSION_TYPES.NONE as string;

const universeContractOptions: RadioOption[] = [
  { label: "Include all Contracts", value: CONTRACT_STATUS_TYPES.ALL },
  {
    label: "Include only active contracts",
    value: CONTRACT_STATUS_TYPES.ACTIVE,
  },
  {
    label:
      "Include active contracts and contracts that ended in the last 2 years",
    value: CONTRACT_STATUS_TYPES.ACTIVE_OR_ENDED_LAST_2_YEARS,
  },
  { label: "Do not include any Contracts", value: NONE_OPTION },
];

export const ContractExportOptions: React.FC<Props> = ({
  inclusion,
  setInclusion,
  universeEnabled,
  contractFieldConfiguration,
  setContractFieldConfiguration,
  enforceMandatoryFields,
  contractStatus = CONTRACT_STATUS_TYPES.ALL,
  setContractStatus,
}) => {
  const handleUniverseOptionChange = (value: string) => {
    if (value === NONE_OPTION) {
      setInclusion?.(EXPORT_INCLUSION_TYPES.NONE);
      setContractStatus?.(CONTRACT_STATUS_TYPES.ALL);
      return;
    }
    setInclusion?.(EXPORT_INCLUSION_TYPES.ALL);
    setContractStatus?.(value as CONTRACT_STATUS_TYPES);
  };

  return (
    <Stack spacing={2} sx={{ width: "70%", paddingTop: "40px" }}>
      <Typography variant="h5" fontWeight={500} fontSize={16}>
        {EXPORT_DATA_TYPES.CONTRACTS}
      </Typography>
      {universeEnabled ? (
        <Box>
          <Typography variant="subtitle1" fontWeight={"bold"}>
            What {EXPORT_DATA_TYPES.CONTRACTS} would you like to see in the
            export?
          </Typography>
          <RadioOptionsInput
            baseId={`CompanyListExport-${EXPORT_DATA_TYPES.CONTRACTS}SelectType`}
            testIdPrefix="company-export-contracts"
            value={
              inclusion === EXPORT_INCLUSION_TYPES.NONE
                ? NONE_OPTION
                : contractStatus
            }
            options={universeContractOptions}
            onChange={handleUniverseOptionChange}
          />
        </Box>
      ) : (
        <ExportDataTypeInclusionSelector
          dataType={EXPORT_DATA_TYPES.CONTRACTS}
          options={getCommonExportRatioOptions(EXPORT_DATA_TYPES.CONTRACTS)}
          inclusion={inclusion}
          setInclusion={setInclusion}
        />
      )}
      {universeEnabled && (
        <FieldConfigurationSelector
          fields={contractExportFields}
          firmographicsFields={firmographicsExportFields}
          value={
            contractFieldConfiguration ?? {
              inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
              fields: [],
            }
          }
          onChange={setContractFieldConfiguration ?? (() => {})}
          title="Contracts Fields"
          checkboxLabel="Export All Contracts Fields"
          // Matches OppGen: the note + force-injection of contract_uuid are only
          // active behind the universe_enforce_mandatory_fields_for_tracking flag.
          requiredField={
            enforceMandatoryFields
              ? {
                  values: ["contract_uuid"],
                  note: "Note: Contract UUID will always be included in your export for proper contract identification.",
                }
              : undefined
          }
          disabled={inclusion === EXPORT_INCLUSION_TYPES.NONE}
        />
      )}
    </Stack>
  );
};
